import { Typography, Box, Tooltip, Chip, Skeleton, Paper } from '@mui/material';
import { Role } from '../../types';
import SecurityIcon from '@mui/icons-material/Security';
import { useRef, useState, useEffect } from 'react';
import { alpha } from '@mui/material/styles';

interface RoleListProps {
  roles: Role[];
  onRoleClick: (role: Role) => void;
  loading?: boolean;
}

const RoleList = ({ roles, onRoleClick, loading = false }: RoleListProps) => {
  if (loading) {
    return <RoleListSkeleton />
  }

  // Sort roles alphabetically by name
  const sortedRoles = [...roles].sort((a, b) => 
    a.name.localeCompare(b.name)
  );

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
        gap: 2.5,
        padding: 2,
      }}
    >
      {sortedRoles.map((role) => (
        <Paper
          key={role._id}
          elevation={0}
          sx={{
            cursor: 'pointer',
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
            height: '100%',
            borderRadius: 1.5,
            backgroundColor: 'background.paper',
            overflow: 'hidden',
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              backgroundColor: 'secondary.main',
              opacity: 0,
              transition: 'opacity 0.2s ease'
            },
            '&:hover': {
              boxShadow: '0 6px 16px rgba(15, 40, 76, 0.1)',
              transform: 'translateY(-2px)',
              '&::before': {
                opacity: 1,
              }
            },
            '&:focus-visible': {
              outline: '2px solid',
              outlineColor: 'secondary.main',
            },
          }}
          onClick={() => onRoleClick(role)}
          tabIndex={0}
          role="button"
          aria-label={`Edit role ${role.name}`}
        >
          <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box 
              sx={{ 
                p: 2.5, 
                backgroundColor: '#0f284c',
                color: 'white',
                borderBottom: '1px solid',
                borderColor: alpha('#ffffff', 0.1)
              }}
            >
              <Box 
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  gap: 1.5
                }}
              >
                <SecurityIcon fontSize="small" />
                <TruncatedText text={role.name} color="white" />
              </Box>
            </Box>
            
            <Box 
              sx={{ 
                p: 2.5, 
                flexGrow: 1,
                backgroundColor: alpha('#f5f8fa', 0.5)
              }}
            >
              <Typography 
                variant="body2" 
                color="text.secondary"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  fontWeight: 500,
                  fontSize: '0.75rem',
                  mb: 1.5
                }}
              >
                <span>Scopes:</span>
                <Chip 
                  size="small" 
                  label={role.scopes?.length || 0} 
                  color="secondary"
                  variant="outlined" 
                  sx={{ 
                    height: 18, 
                    fontSize: '0.65rem',
                    fontWeight: 600,
                    borderRadius: '4px'
                  }}
                />
              </Typography>
              <Box 
                sx={{ 
                  p: 1.5, 
                  bgcolor: '#ffffff', 
                  borderRadius: 1.5,
                  boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.05)',
                  maxHeight: '110px',
                  overflowY: 'auto',
                  '&::-webkit-scrollbar': {
                    width: '4px',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: 'rgba(0,0,0,0.15)',
                    borderRadius: '4px',
                  },
                  '&:empty::before': {
                    content: '"No scopes"',
                    color: 'text.disabled',
                    fontStyle: 'italic'
                  }
                }}
              >
                {role.scopes && role.scopes.length > 0 ? (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                    {role.scopes.map((scope, index) => (
                      <Chip
                        key={index}
                        label={scope.name}
                        size="small"
                        sx={{ 
                          fontSize: '0.65rem',
                          height: 22,
                          borderRadius: '4px',
                          backgroundColor: alpha('#0f284c', 0.06),
                          color: '#0f284c',
                          fontWeight: 500,
                          '& .MuiChip-label': {
                            px: 1
                          }
                        }}
                      />
                    ))}
                  </Box>
                ) : (
                  <Typography variant="caption" sx={{ fontSize: '0.75rem', color: 'text.disabled', fontStyle: 'italic' }}>
                    No specific scopes
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
        </Paper>
      ))}
    </Box>
  );
};

// Skeleton loader for the role list
const RoleListSkeleton = () => {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
        gap: 2.5,
        padding: 2,
      }}
    >
      {[1, 2, 3, 4].map((index) => (
        <Paper
          key={index}
          elevation={0}
          sx={{
            cursor: 'pointer',
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
            height: '100%',
            borderRadius: 1.5,
            backgroundColor: 'background.paper',
            overflow: 'hidden',
            position: 'relative',
            boxShadow: '0 2px 8px rgba(15, 40, 76, 0.05)',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              backgroundColor: 'secondary.main',
              opacity: 0,
            },
          }}
        >
          <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box 
              sx={{ 
                p: 2.5, 
                backgroundColor: '#0f284c',
                color: 'white',
                borderBottom: '1px solid',
                borderColor: alpha('#ffffff', 0.1)
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Skeleton variant="circular" width={20} height={20} sx={{ bgcolor: alpha('#ffffff', 0.1) }} />
                <Skeleton variant="text" width={140} height={24} sx={{ bgcolor: alpha('#ffffff', 0.1) }} />
              </Box>
            </Box>
            
            <Box 
              sx={{ 
                p: 2.5, 
                flexGrow: 1,
                backgroundColor: alpha('#f5f8fa', 0.5)
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1.5 }}>
                <Skeleton variant="text" width={50} height={20} />
                <Skeleton variant="rounded" width={24} height={18} sx={{ borderRadius: '4px' }} />
              </Box>
              <Skeleton 
                variant="rectangular" 
                width="100%" 
                height={110}
                sx={{ 
                  borderRadius: 1.5,
                  bgcolor: '#ffffff',
                  boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.05)'
                }} 
              />
            </Box>
          </Box>
        </Paper>
      ))}
    </Box>
  );
};

// Component that only shows tooltip when text is truncated
const TruncatedText = ({ text, color = 'inherit' }: { text: string; color?: string }) => {
  const textRef = useRef<HTMLSpanElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    const checkTruncation = () => {
      const element = textRef.current;
      if (element) {
        setIsTruncated(element.scrollWidth > element.clientWidth);
      }
    };
    
    checkTruncation();
    window.addEventListener('resize', checkTruncation);
    
    return () => {
      window.removeEventListener('resize', checkTruncation);
    };
  }, [text]);

  const typographyProps = {
    variant: "subtitle1" as const,
    component: "div" as const,
    ref: textRef,
    sx: {
      maxWidth: '210px',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      fontWeight: 500,
      letterSpacing: '0.01em',
      color
    }
  };

  return isTruncated ? (
    <Tooltip title={text} placement="top">
      <Typography {...typographyProps}>
        {text}
      </Typography>
    </Tooltip>
  ) : (
    <Typography {...typographyProps}>
      {text}
    </Typography>
  );
};

export default RoleList;